// Designer/admin authoring UI for a single `ParametricCabinetClass`.
//
// Tab-based form on top of the same draft state used to back the JSON
// textarea. The Form/JSON toggle lets power users drop down to raw
// JSON; switching directions serialises/deserialises so the two views
// stay in sync.
//
// ExprInput is the workhorse: every expression-string field uses it,
// so a typo in `back.box.min.x = "T - rabet"` shows the parse error
// inline and the evaluated value at the preview dims renders next to
// it. That keeps designers in the form without forcing a mental
// pre-eval pass.

import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";

import type {
  CabinetAnchor,
  CabinetMeshChunk,
  Grain,
  JoineryEdge,
  JoineryKind,
  ParametricCabinetClass,
  PartDef,
  PartRole,
} from "@/lib/types";
import { ExprError, evalAst, evalParams, parse, type Scope } from "@/lib/cabinets/dsl/expr";
import { evaluateClass } from "@/lib/cabinets/dsl/evaluate";
import { PartsPreview2D } from "./PartsPreview2D";

// ── Constants ──────────────────────────────────────────────────

const PART_ROLES: PartRole[] = [
  "side_left", "side_right", "top", "bottom", "back",
  "stretcher_front", "stretcher_back", "shelf",
  "door", "door_left", "door_right",
  "toe_kick", "filler", "custom",
];
const MATERIAL_SLOTS: PartDef["materialSlot"][] = ["carcass", "back", "door", "shelf", "toe", "custom"];
const GRAINS: Grain[] = ["none", "length", "width"];
const JOINERY_KINDS: JoineryKind[] = ["butt", "rabbet", "dado", "slanted_back"];
const ANCHORS: CabinetAnchor[] = ["floor", "wall", "ceiling"];
const CATEGORIES: ParametricCabinetClass["category"][] = ["base", "wall", "tall", "custom"];
const FACES = ["xMin", "xMax", "yMin", "yMax", "zMin", "zMax"] as const;

// ── Top-level editor ───────────────────────────────────────────

export interface CabinetClassEditorProps {
  /** Draft class being edited. Treat as immutable; emit changes via onChange. */
  klass: ParametricCabinetClass;
  /** Whether the form is allowed to mutate the class (false for system seed). */
  readOnly?: boolean;
  /** Scope used to live-evaluate expression inputs. Caller injects
   *  preview dims here so designers see the resulting inches as they type. */
  previewScope: Scope;
  onChange: (next: ParametricCabinetClass) => void;
}

type Tab = "header" | "params" | "parts";
type Mode = "form" | "json";

export function CabinetClassEditor({
  klass,
  readOnly = false,
  previewScope,
  onChange,
}: CabinetClassEditorProps) {
  const [tab, setTab] = useState<Tab>("header");
  const [mode, setMode] = useState<Mode>("form");

  return (
    <div style={{ display: "grid", gridTemplateRows: "auto 1fr", gap: 8, minHeight: 0 }}>
      <div
        role="tablist"
        style={{
          display: "flex", gap: 4, alignItems: "center",
          borderBottom: "1px solid var(--line)",
        }}
      >
        {(["header", "params", "parts"] as Tab[]).map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t && mode === "form"}
            disabled={mode !== "form"}
            onClick={() => setTab(t)}
            style={{
              padding: "6px 12px",
              fontSize: 12,
              background: "transparent",
              border: "none",
              borderBottom: tab === t && mode === "form" ? "2px solid var(--accent, #ffaa55)" : "2px solid transparent",
              color: mode === "form" ? "var(--fg)" : "var(--fg-dim)",
              cursor: mode === "form" ? "pointer" : "default",
              textTransform: "capitalize",
            }}
          >
            {t}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <ModeToggle mode={mode} onChange={setMode} />
      </div>

      <div style={{ overflow: "auto", minHeight: 0, padding: "8px 4px" }}>
        {mode === "json" ? (
          <JsonView klass={klass} readOnly={readOnly} onChange={onChange} />
        ) : tab === "header" ? (
          <HeaderTab klass={klass} readOnly={readOnly} onChange={onChange} />
        ) : tab === "params" ? (
          <ParamsTab klass={klass} readOnly={readOnly} onChange={onChange} previewScope={previewScope} />
        ) : (
          <PartsTab klass={klass} readOnly={readOnly} onChange={onChange} previewScope={previewScope} />
        )}
      </div>
    </div>
  );
}

function ModeToggle({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  return (
    <div style={{ display: "flex", border: "1px solid var(--line)", borderRadius: 4, fontSize: 11 }}>
      {(["form", "json"] as Mode[]).map((m) => (
        <button
          key={m}
          onClick={() => onChange(m)}
          style={{
            padding: "3px 10px",
            background: mode === m ? "rgba(255,170,85,0.10)" : "transparent",
            color: mode === m ? "var(--accent, #ffaa55)" : "var(--fg-dim)",
            border: "none",
            cursor: "pointer",
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >{m}</button>
      ))}
    </div>
  );
}

// ── Reusable expression input ──────────────────────────────────

interface ExprInputProps {
  value: string | number;
  onChange: (next: string) => void;
  scope?: Scope;
  placeholder?: string;
  width?: number | string;
  monospace?: boolean;
  disabled?: boolean;
  /** Hide the inline `= 12.34″` evaluation hint (e.g. for boolean expressions). */
  hideEvalHint?: boolean;
}

export function ExprInput({
  value, onChange, scope, placeholder, width, monospace = true, disabled, hideEvalHint,
}: ExprInputProps) {
  const [text, setText] = useState(String(value));
  const [error, setError] = useState<string | null>(null);
  const [evalResult, setEvalResult] = useState<number | null>(null);
  // Re-sync from external value when caller commits a different value
  // (e.g. after a sibling field change re-evaluates derived state).
  const lastExternal = useRef<string>(String(value));
  useEffect(() => {
    const incoming = String(value);
    if (incoming !== lastExternal.current) {
      lastExternal.current = incoming;
      setText(incoming);
    }
  }, [value]);

  // Continuous validation: parse on every keystroke, evaluate against
  // scope. Both errors are non-blocking — the user can keep typing.
  useEffect(() => {
    if (!text.trim()) {
      setError(null);
      setEvalResult(null);
      return;
    }
    try {
      const ast = parse(text);
      setError(null);
      if (scope) {
        try {
          const r = evalAst(ast, scope);
          setEvalResult(Number.isFinite(r) ? r : null);
        } catch {
          setEvalResult(null);
        }
      } else {
        setEvalResult(null);
      }
    } catch (e) {
      const msg = e instanceof ExprError ? e.message.replace(/^expr@\d+: /, "")
                : e instanceof Error ? e.message : "parse error";
      setError(msg);
      setEvalResult(null);
    }
  }, [text, scope]);

  function commit() {
    const stripped = text.trim();
    const prev = String(value).trim();
    if (stripped !== prev) {
      lastExternal.current = stripped;
      onChange(stripped);
    }
  }

  return (
    <span style={{ display: "inline-flex", flexDirection: "column", width: width ?? "100%", minWidth: 0 }}>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur(); }}
        disabled={disabled}
        placeholder={placeholder}
        spellCheck={false}
        style={{
          width: "100%",
          fontFamily: monospace ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined,
          fontSize: 11,
          padding: "2px 6px",
          borderColor: error ? "var(--danger)" : undefined,
          background: disabled ? "rgba(255,255,255,0.02)" : undefined,
        }}
      />
      {(error || (evalResult != null && !hideEvalHint)) && (
        <span style={{ fontSize: 10, marginTop: 1, paddingLeft: 6, lineHeight: 1.2 }}>
          {error ? (
            <span style={{ color: "var(--danger)" }}>{error}</span>
          ) : (
            <span className="muted">= {fmt(evalResult!)}</span>
          )}
        </span>
      )}
    </span>
  );
}

function fmt(n: number): string {
  return Math.abs(n - Math.round(n)) < 1e-6 ? String(n) : n.toFixed(3);
}

// ── Header tab ─────────────────────────────────────────────────

function HeaderTab({
  klass, readOnly, onChange,
}: {
  klass: ParametricCabinetClass;
  readOnly: boolean;
  onChange: (next: ParametricCabinetClass) => void;
}) {
  function set<K extends keyof ParametricCabinetClass>(key: K, v: ParametricCabinetClass[K]) {
    onChange({ ...klass, [key]: v });
  }
  function setDim(axis: "w" | "h" | "d", v: number) {
    onChange({ ...klass, defaults: { ...klass.defaults, [axis]: v } });
  }
  function setBound(axis: "w" | "h" | "d", idx: 0 | 1, v: number) {
    const cur = klass.bounds[axis];
    const next: [number, number] = idx === 0 ? [v, cur[1]] : [cur[0], v];
    onChange({ ...klass, bounds: { ...klass.bounds, [axis]: next } });
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 12, rowGap: 8, fontSize: 12, alignItems: "center", maxWidth: 560 }}>
      <Row label="ID">
        <input
          value={klass.id}
          onChange={(e) => set("id", e.target.value)}
          disabled={readOnly || klass.system}
          spellCheck={false}
          style={{ width: 220, fontFamily: "ui-monospace, monospace", fontSize: 12 }}
          title={klass.system ? "System class id is fixed" : "Stable identifier, used in Cabinet.classId"}
        />
      </Row>
      <Row label="Label">
        <input
          value={klass.label}
          onChange={(e) => set("label", e.target.value)}
          disabled={readOnly}
          style={{ width: 280, fontSize: 12 }}
        />
      </Row>
      <Row label="Category">
        <select value={klass.category} onChange={(e) => set("category", e.target.value as ParametricCabinetClass["category"])} disabled={readOnly} style={{ fontSize: 12 }}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Row>
      <Row label="Anchor">
        <select value={klass.anchor} onChange={(e) => set("anchor", e.target.value as CabinetAnchor)} disabled={readOnly} style={{ fontSize: 12 }}>
          {ANCHORS.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </Row>

      <Row label="Defaults (″)">
        <div style={{ display: "flex", gap: 6 }}>
          {(["w", "h", "d"] as const).map((axis) => (
            <label key={axis} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
              <span className="muted" style={{ textTransform: "uppercase" }}>{axis}</span>
              <input
                type="number" step={0.5}
                value={klass.defaults[axis]}
                onChange={(e) => setDim(axis, Number(e.target.value))}
                disabled={readOnly}
                style={{ width: 70, fontSize: 12 }}
              />
            </label>
          ))}
        </div>
      </Row>
      <Row label="Bounds (min / max ″)">
        <div style={{ display: "grid", gridTemplateColumns: "auto auto auto", rowGap: 4, columnGap: 6, fontSize: 11 }}>
          {(["w", "h", "d"] as const).map((axis) => (
            <Row key={axis} label={axis.toUpperCase()} mini>
              <input type="number" step={0.5} value={klass.bounds[axis][0]} onChange={(e) => setBound(axis, 0, Number(e.target.value))} disabled={readOnly} style={{ width: 70, fontSize: 12 }} />
              <input type="number" step={0.5} value={klass.bounds[axis][1]} onChange={(e) => setBound(axis, 1, Number(e.target.value))} disabled={readOnly} style={{ width: 70, fontSize: 12 }} />
            </Row>
          ))}
        </div>
      </Row>

      <Row label="System">
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
          <input type="checkbox" checked={klass.system} onChange={(e) => set("system", e.target.checked)} disabled={readOnly} style={{ width: "auto" }} />
          <span className="muted">Shipped with the SPA; UI hides delete and warns on edit.</span>
        </label>
      </Row>
      <Row label="Locked">
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
          <input type="checkbox" checked={klass.locked} onChange={(e) => set("locked", e.target.checked)} disabled={readOnly} style={{ width: "auto" }} />
          <span className="muted">Frozen against further edits even if not a system class.</span>
        </label>
      </Row>
    </div>
  );
}

function Row({ label, children, mini = false }: { label: string; children: React.ReactNode; mini?: boolean }) {
  return (
    <>
      <label className="muted" style={{ fontSize: mini ? 10 : 11, textTransform: mini ? "uppercase" : undefined, letterSpacing: mini ? 0.4 : undefined }}>
        {label}
      </label>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>{children}</div>
    </>
  );
}

// ── Params tab ─────────────────────────────────────────────────
//
// Params are a name→(number | expression) map whose **declaration
// order matters** because later params can reference earlier ones
// (carcassH = "H - TOE_KICK_H"). We preserve order via the standard
// JS object insertion semantics.

function ParamsTab({
  klass, readOnly, onChange, previewScope,
}: {
  klass: ParametricCabinetClass;
  readOnly: boolean;
  onChange: (next: ParametricCabinetClass) => void;
  previewScope: Scope;
}) {
  const entries = Object.entries(klass.params);
  // Resolved scope as params evaluate top-to-bottom — used as the live
  // hint for each subsequent row.
  const resolvedScopes = useMemo<Scope[]>(() => {
    const scopes: Scope[] = [previewScope];
    let cur: Scope = { ...previewScope };
    for (const [name, src] of entries) {
      try {
        const next = evalParams({ [name]: src }, cur);
        cur = next;
      } catch {
        // leave cur unchanged; downstream rows lose this var but show error inline
      }
      scopes.push(cur);
    }
    return scopes;
  }, [entries, previewScope]);

  function commit(next: [string, string | number][]) {
    const out: Record<string, string | number> = {};
    for (const [k, v] of next) {
      if (!k) continue;
      out[k] = v;
    }
    onChange({ ...klass, params: out });
  }

  function setName(idx: number, name: string) {
    const next = entries.slice();
    next[idx] = [name, next[idx]![1]];
    commit(next);
  }
  function setValue(idx: number, raw: string) {
    const next = entries.slice();
    const asNum = Number(raw);
    next[idx] = [next[idx]![0], Number.isFinite(asNum) && raw.trim() !== "" && /^-?[\d.]+$/.test(raw.trim()) ? asNum : raw];
    commit(next);
  }
  function add() {
    const base = "param";
    let name = base;
    let i = 1;
    const seen = new Set(entries.map(([k]) => k));
    while (seen.has(name)) name = `${base}${i++}`;
    commit([...entries, [name, 0]]);
  }
  function removeAt(idx: number) {
    commit(entries.filter((_, i) => i !== idx));
  }
  function move(idx: number, dir: -1 | 1) {
    const dst = idx + dir;
    if (dst < 0 || dst >= entries.length) return;
    const next = entries.slice();
    [next[idx], next[dst]] = [next[dst]!, next[idx]!];
    commit(next);
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <div className="muted" style={{ fontSize: 11, marginBottom: 8 }}>
        Order matters — later params can reference earlier ones
        (e.g. <code>carcassH = "H - TOE_KICK_H"</code>). Numeric values can be edited directly;
        anything containing letters or operators is treated as an expression.
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr style={{ color: "var(--fg-dim)" }}>
            <th style={th}>Name</th>
            <th style={th}>Value / expression</th>
            <th style={{ ...th, width: 80 }}></th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([name, val], idx) => (
            <tr key={idx} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
              <td style={td}>
                <input
                  value={name}
                  onChange={(e) => setName(idx, e.target.value)}
                  disabled={readOnly}
                  spellCheck={false}
                  style={{ width: "100%", fontFamily: "ui-monospace, monospace", fontSize: 11 }}
                />
              </td>
              <td style={td}>
                <ExprInput
                  value={val}
                  onChange={(v) => setValue(idx, v)}
                  scope={resolvedScopes[idx]}
                  disabled={readOnly}
                />
              </td>
              <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                <button className="ghost" style={btnIcon} onClick={() => move(idx, -1)} disabled={readOnly || idx === 0} title="Move up">▲</button>
                <button className="ghost" style={btnIcon} onClick={() => move(idx, +1)} disabled={readOnly || idx === entries.length - 1} title="Move down">▼</button>
                <button className="ghost" style={{ ...btnIcon, color: "var(--danger)" }} onClick={() => removeAt(idx)} disabled={readOnly} title="Delete">×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="ghost" style={{ fontSize: 11, marginTop: 8 }} onClick={add} disabled={readOnly}>+ Add param</button>
    </div>
  );
}

// ── Parts tab ──────────────────────────────────────────────────

function PartsTab({
  klass, readOnly, onChange, previewScope,
}: {
  klass: ParametricCabinetClass;
  readOnly: boolean;
  onChange: (next: ParametricCabinetClass) => void;
  previewScope: Scope;
}) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const part = klass.parts[selectedIdx] ?? null;

  // Scope for the selected part: preview dims + resolved class params.
  const partScope = useMemo<Scope>(() => {
    try {
      return evalParams(klass.params, previewScope);
    } catch {
      return previewScope;
    }
  }, [klass.params, previewScope]);

  // Evaluate the class at the preview dims to get 3D boxes for each
  // emitted mesh chunk. We re-evaluate per render — cheap for typical
  // class sizes (~15 parts) and keeps the preview live as the user
  // edits expressions in the form below.
  const meshChunks = useMemo<CabinetMeshChunk[]>(() => {
    const W = (previewScope.W as number) ?? klass.defaults.w;
    const H = (previewScope.H as number) ?? klass.defaults.h;
    const D = (previewScope.D as number) ?? klass.defaults.d;
    try {
      const r = evaluateClass(
        { width: W, height: H, depth: D, classParams: {}, partOverrides: {} },
        klass,
      );
      return r.meshes;
    } catch {
      return [];
    }
  }, [klass, previewScope]);

  function selectById(id: string) {
    const i = klass.parts.findIndex((p) => p.id === id);
    if (i >= 0) setSelectedIdx(i);
  }

  function patchPart(idx: number, patch: Partial<PartDef>) {
    const next = klass.parts.slice();
    next[idx] = { ...next[idx]!, ...patch };
    onChange({ ...klass, parts: next });
  }
  function addPart() {
    onChange({ ...klass, parts: [...klass.parts, blankPart(klass.parts.length)] });
    setSelectedIdx(klass.parts.length);
  }
  function removePart(idx: number) {
    if (!confirm(`Remove part "${klass.parts[idx]?.id}"?`)) return;
    const next = klass.parts.filter((_, i) => i !== idx);
    onChange({ ...klass, parts: next });
    setSelectedIdx(Math.max(0, Math.min(idx, next.length - 1)));
  }
  function duplicatePart(idx: number) {
    const src = klass.parts[idx];
    if (!src) return;
    const copy: PartDef = { ...structuredClone(src), id: `${src.id}_copy` };
    const next = [...klass.parts.slice(0, idx + 1), copy, ...klass.parts.slice(idx + 1)];
    onChange({ ...klass, parts: next });
    setSelectedIdx(idx + 1);
  }

  return (
    <div style={{ display: "grid", gridTemplateRows: "auto 1fr", gap: 8, minHeight: 0 }}>
      <PartsPreview2D
        meshes={meshChunks}
        cabinetW={(previewScope.W as number) ?? klass.defaults.w}
        cabinetH={(previewScope.H as number) ?? klass.defaults.h}
        cabinetD={(previewScope.D as number) ?? klass.defaults.d}
        selectedId={part?.id ?? null}
        onSelect={selectById}
      />
      <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 12, minHeight: 0 }}>
      <aside style={{ borderRight: "1px solid var(--line)", paddingRight: 8, overflowY: "auto" }}>
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {klass.parts.map((p, idx) => (
            <li key={idx}>
              <button
                onClick={() => setSelectedIdx(idx)}
                style={{
                  width: "100%", textAlign: "left", padding: "4px 6px",
                  fontSize: 11,
                  border: "1px solid transparent",
                  borderColor: idx === selectedIdx ? "var(--accent, #ffaa55)" : "transparent",
                  background: idx === selectedIdx ? "rgba(255,170,85,0.10)" : "transparent",
                  borderRadius: 4, marginBottom: 2,
                  display: "flex", justifyContent: "space-between", gap: 6,
                  fontFamily: "ui-monospace, monospace",
                }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.id}</span>
                <span className="muted" style={{ fontSize: 10 }}>{p.role}</span>
              </button>
            </li>
          ))}
        </ul>
        <button className="ghost" onClick={addPart} disabled={readOnly} style={{ fontSize: 11, marginTop: 6, width: "100%" }}>
          + Add part
        </button>
      </aside>

      <div style={{ overflowY: "auto", minWidth: 0 }}>
        {part ? (
          <PartForm
            part={part}
            otherIds={klass.parts.filter((_, i) => i !== selectedIdx).map((p) => p.id)}
            readOnly={readOnly}
            scope={partScope}
            onChange={(patch) => patchPart(selectedIdx, patch)}
            onDuplicate={() => duplicatePart(selectedIdx)}
            onDelete={() => removePart(selectedIdx)}
          />
        ) : (
          <div className="muted" style={{ fontSize: 11, padding: 12 }}>
            No parts yet. Add one with the button on the left.
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

// ── Per-part form ──────────────────────────────────────────────

function PartForm({
  part, otherIds, readOnly, scope, onChange, onDuplicate, onDelete,
}: {
  part: PartDef;
  otherIds: string[];
  readOnly: boolean;
  scope: Scope;
  onChange: (patch: Partial<PartDef>) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  function setBoxCorner(corner: "min" | "max", axis: 0 | 1 | 2, v: string) {
    const arr: [string, string, string] = [...part.box[corner]];
    arr[axis] = v;
    onChange({ box: { ...part.box, [corner]: arr } });
  }
  function setCut(axis: "w" | "h", v: string) {
    onChange({ cut: { ...part.cut, [axis]: v } });
  }
  function setJoineryField(face: typeof FACES[number], patch: Partial<JoineryEdge> | null) {
    const joinery = { ...(part.joinery ?? {}) };
    if (patch === null) {
      delete joinery[face];
    } else {
      joinery[face] = { ...(joinery[face] ?? { kind: "butt" }), ...patch };
    }
    onChange({ joinery: Object.keys(joinery).length ? joinery : undefined });
  }

  const joinery = part.joinery ?? {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "0 8px 24px" }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <h4 style={{ margin: 0, fontFamily: "ui-monospace, monospace" }}>{part.id}</h4>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="ghost" style={{ fontSize: 11 }} onClick={onDuplicate} disabled={readOnly}>Duplicate</button>
          <button className="ghost" style={{ fontSize: 11, color: "var(--danger)" }} onClick={onDelete} disabled={readOnly}>Delete</button>
        </div>
      </header>

      <Section title="Identity">
        <Field label="ID">
          <input value={part.id} onChange={(e) => onChange({ id: e.target.value })} disabled={readOnly} spellCheck={false} style={{ width: 220, fontFamily: "ui-monospace, monospace", fontSize: 12 }} />
        </Field>
        <Field label="Name">
          <input value={part.name} onChange={(e) => onChange({ name: e.target.value })} disabled={readOnly} style={{ width: 280, fontSize: 12 }} />
        </Field>
        <Field label="Role">
          <select value={part.role} onChange={(e) => onChange({ role: e.target.value as PartRole })} disabled={readOnly} style={{ fontSize: 12 }}>
            {PART_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Enabled (expr)">
          <ExprInput
            value={part.enabled ?? ""}
            onChange={(v) => onChange({ enabled: v || undefined })}
            scope={scope}
            placeholder="true"
            width={220}
            disabled={readOnly}
            hideEvalHint
          />
        </Field>
      </Section>

      <Section title="Geometry (cabinet-local inches)">
        <Field label="Box min">
          <ExprTriple corner={part.box.min} scope={scope} disabled={readOnly} onChange={(axis, v) => setBoxCorner("min", axis, v)} />
        </Field>
        <Field label="Box max">
          <ExprTriple corner={part.box.max} scope={scope} disabled={readOnly} onChange={(axis, v) => setBoxCorner("max", axis, v)} />
        </Field>
        <Field label="Thickness">
          <ExprInput value={part.thickness} onChange={(v) => onChange({ thickness: v })} scope={scope} width={160} disabled={readOnly} />
        </Field>
        <Field label="Cut size">
          <span style={{ display: "flex", gap: 8 }}>
            <ExprInput value={part.cut.w} onChange={(v) => setCut("w", v)} scope={scope} width={160} disabled={readOnly} />
            <span className="muted">×</span>
            <ExprInput value={part.cut.h} onChange={(v) => setCut("h", v)} scope={scope} width={160} disabled={readOnly} />
          </span>
        </Field>
      </Section>

      <Section title="Material & appearance">
        <Field label="Material slot">
          <select value={part.materialSlot} onChange={(e) => onChange({ materialSlot: e.target.value as PartDef["materialSlot"] })} disabled={readOnly} style={{ fontSize: 12 }}>
            {MATERIAL_SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Grain">
          <select value={part.grain} onChange={(e) => onChange({ grain: e.target.value as Grain })} disabled={readOnly} style={{ fontSize: 12 }}>
            {GRAINS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </Field>
        <Field label="Mesh color">
          <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input type="color" value={part.color ?? "#dec3a4"} onChange={(e) => onChange({ color: e.target.value })} disabled={readOnly} style={{ width: 32, height: 22, padding: 0, border: "1px solid var(--line)" }} />
            <input type="text" value={part.color ?? ""} onChange={(e) => onChange({ color: e.target.value || undefined })} disabled={readOnly} placeholder="(material slot default)" style={{ width: 120, fontFamily: "ui-monospace, monospace", fontSize: 11 }} />
          </span>
        </Field>
      </Section>

      <Section title="Joinery (per cabinet-local face)">
        <div style={{ display: "grid", gridTemplateColumns: "60px 110px 160px 1fr 24px", gap: 4, fontSize: 11, alignItems: "center" }}>
          <span className="muted">Face</span>
          <span className="muted">Kind</span>
          <span className="muted">Mates with</span>
          <span className="muted">Depth (expr)</span>
          <span></span>
          {FACES.map((face) => {
            const edge = joinery[face];
            return (
              <JoineryRow
                key={face}
                face={face}
                edge={edge}
                otherIds={otherIds}
                scope={scope}
                disabled={readOnly}
                onChange={(patch) => setJoineryField(face, patch)}
              />
            );
          })}
        </div>
      </Section>

      <Section title="Repeat">
        <Field label="Count (expr)">
          <ExprInput
            value={part.repeat?.count ?? ""}
            onChange={(v) => {
              if (!v) { onChange({ repeat: undefined }); return; }
              const cur = part.repeat ?? { count: v, var: "i" };
              onChange({ repeat: { ...cur, count: v } });
            }}
            scope={scope}
            placeholder="(no repeat)"
            width={160}
            disabled={readOnly}
          />
        </Field>
        {part.repeat && (
          <Field label="Loop variable">
            <input
              value={part.repeat.var}
              onChange={(e) => onChange({ repeat: { ...part.repeat!, var: e.target.value || "i" } })}
              disabled={readOnly}
              style={{ width: 80, fontFamily: "ui-monospace, monospace", fontSize: 12 }}
            />
          </Field>
        )}
      </Section>

      <Section title="Drilling templates">
        {part.drilling?.length ? (
          part.drilling.map((d, i) => (
            <div key={i} className="muted" style={{ fontSize: 11, padding: "4px 8px", border: "1px solid var(--line)", borderRadius: 4 }}>
              <code>{d.kind}</code>{" "}
              {Object.entries(d).filter(([k]) => k !== "kind").map(([k, v]) => `${k}=${String(v)}`).join(" · ") || "(no params)"}
            </div>
          ))
        ) : (
          <div className="muted" style={{ fontSize: 11 }}>No drillings. Switch to JSON to add custom drilling templates.</div>
        )}
      </Section>
    </div>
  );
}

function ExprTriple({
  corner, scope, disabled, onChange,
}: {
  corner: [string, string, string];
  scope: Scope;
  disabled: boolean;
  onChange: (axis: 0 | 1 | 2, v: string) => void;
}) {
  return (
    <span style={{ display: "grid", gridTemplateColumns: "auto auto auto", gap: 6 }}>
      {[0, 1, 2].map((axis) => (
        <ExprInput
          key={axis}
          value={corner[axis as 0 | 1 | 2]}
          onChange={(v) => onChange(axis as 0 | 1 | 2, v)}
          scope={scope}
          width={140}
          disabled={disabled}
        />
      ))}
    </span>
  );
}

function JoineryRow({
  face, edge, otherIds, scope, disabled, onChange,
}: {
  face: typeof FACES[number];
  edge: JoineryEdge | undefined;
  otherIds: string[];
  scope: Scope;
  disabled: boolean;
  onChange: (patch: Partial<JoineryEdge> | null) => void;
}) {
  const present = !!edge;
  return (
    <>
      <span className="muted" style={{ fontSize: 10, fontFamily: "ui-monospace, monospace" }}>{face}</span>
      {present ? (
        <>
          <select
            value={edge!.kind}
            onChange={(e) => onChange({ kind: e.target.value as JoineryKind })}
            disabled={disabled}
            style={{ fontSize: 11 }}
          >
            {JOINERY_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <select
            value={edge!.matesWith ?? ""}
            onChange={(e) => onChange({ matesWith: e.target.value || undefined })}
            disabled={disabled}
            style={{ fontSize: 11 }}
          >
            <option value="">— none —</option>
            {otherIds.map((id) => <option key={id} value={id}>{id}</option>)}
          </select>
          <ExprInput
            value={edge!.depth ?? ""}
            onChange={(v) => onChange({ depth: v || undefined })}
            scope={scope}
            placeholder="(no depth)"
            disabled={disabled}
          />
          <button className="ghost" style={btnIcon} onClick={() => onChange(null)} disabled={disabled} title="Remove">×</button>
        </>
      ) : (
        <>
          <button className="ghost" style={{ fontSize: 11, gridColumn: "2 / 5", textAlign: "left" }} onClick={() => onChange({ kind: "butt" })} disabled={disabled}>
            + add joinery
          </button>
          <span />
        </>
      )}
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <h5 style={{ margin: 0, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--fg-dim)" }}>{title}</h5>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", alignItems: "center", gap: 8 }}>
      <span className="muted" style={{ fontSize: 11 }}>{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>{children}</div>
    </div>
  );
}

// ── JSON view (fallback for power users) ───────────────────────

function JsonView({
  klass, readOnly, onChange,
}: {
  klass: ParametricCabinetClass;
  readOnly: boolean;
  onChange: (next: ParametricCabinetClass) => void;
}) {
  const [text, setText] = useState<string>(() => JSON.stringify(klass, null, 2));
  const [error, setError] = useState<string | null>(null);

  // Re-sync only when the EXTERNAL object identity changes (e.g. user
  // picked a different class) — not when our own commit round-trips.
  const lastSeen = useRef(klass);
  useEffect(() => {
    if (lastSeen.current !== klass) {
      lastSeen.current = klass;
      setText(JSON.stringify(klass, null, 2));
      setError(null);
    }
  }, [klass]);

  function commit(next: string) {
    setText(next);
    try {
      const parsed = JSON.parse(next) as ParametricCabinetClass;
      setError(null);
      lastSeen.current = parsed;
      onChange(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div style={{ display: "grid", gridTemplateRows: "1fr auto", height: "100%", gap: 4, minHeight: 400 }}>
      <textarea
        value={text}
        onChange={(e) => commit(e.target.value)}
        disabled={readOnly}
        spellCheck={false}
        style={{
          width: "100%", height: "100%", minHeight: 360,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 12,
          padding: 12,
          border: "1px solid var(--line)",
          borderRadius: 4,
          background: readOnly ? "rgba(255,255,255,0.02)" : "transparent",
          resize: "none",
        }}
      />
      {error && <div style={{ color: "var(--danger)", fontSize: 11 }}>{error}</div>}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────

function blankPart(idx: number): PartDef {
  return {
    id: `part_${idx + 1}`,
    name: `Part ${idx + 1}`,
    role: "custom",
    box: { min: ["0", "0", "0"], max: ["T", "T", "T"] },
    thickness: "T",
    materialSlot: "carcass",
    grain: "length",
    cut: { w: "T", h: "T" },
  };
}

// ── Tiny shared styles ─────────────────────────────────────────

const th: CSSProperties = { textAlign: "left", padding: "4px 6px", borderBottom: "1px solid var(--line)", fontWeight: 500 };
const td: CSSProperties = { padding: "2px 6px", verticalAlign: "top" };
const btnIcon: CSSProperties = { padding: "0 4px", fontSize: 11, minWidth: 18 };
